
import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Download, User, Mail, Phone, MapPin, School, Briefcase, Star, FileText, ChevronRight } from 'lucide-react';
import jsPDF from 'jspdf';

interface Profile {
  fullName: string;
  email: string;
  phone: string;
  location: string;
  educationLevel: string;
  skills: string[];
  registeredAt: string;
}

interface CVAssistantProps {
  savedProfiles: Profile[];
  registrationData: any;
  hasPaidForCV: boolean;
  onPaymentRequired: () => void;
}

const CVAssistant: React.FC<CVAssistantProps> = ({ savedProfiles, registrationData, hasPaidForCV, onPaymentRequired }) => {
  const [cvData, setCVData] = useState({
    fullName: registrationData?.fullName || '',
    email: registrationData?.email || '',
    phone: registrationData?.phone || '',
    location: registrationData?.location || '',
    educationLevel: registrationData?.educationLevel || 'ordinary',
    education: '',
    skills: '',
    experience: '',
    summary: '',
  });
  const [previewMode, setPreviewMode] = useState(false);
  const cvRef = useRef<HTMLDivElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCVData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPreviewMode(true);
  };

  const downloadPDF = () => {
    if (!hasPaidForCV) {
      alert('Please pay the $3 CV download fee to download the PDF.');
      return;
    }
    if (!cvRef.current) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;

    // Header with gradient color (simulated with dark blue)
    doc.setFillColor(30, 58, 138);
    doc.rect(0, 0, pageWidth, 40, 'F');

    // Name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text(cvData.fullName || 'Your Name', pageWidth / 2, yPos + 15, { align: 'center' });

    // Title line
    yPos += 25;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const contactLine = [
      cvData.email,
      cvData.phone,
      cvData.location,
    ].filter(Boolean).join('  |  ');
    doc.text(contactLine, pageWidth / 2, yPos, { align: 'center' });

    yPos += 15;

    // Divider
    doc.setDrawColor(200, 200, 200);
    doc.line(15, yPos, pageWidth - 15, yPos);
    yPos += 10;

    // Summary Section
    if (cvData.summary) {
      doc.setTextColor(30, 58, 138);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('PROFESSIONAL SUMMARY', 15, yPos);
      yPos += 2;
      doc.setDrawColor(30, 58, 138);
      doc.line(15, yPos, 45, yPos);
      yPos += 6;

      doc.setTextColor(60, 60, 60);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const summaryLines = doc.splitTextToSize(cvData.summary, pageWidth - 30);
      doc.text(summaryLines, 15, yPos);
      yPos += summaryLines.length * 5 + 8;
    }

    // Skills Section
    if (cvData.skills) {
      if (yPos > 260) {
        doc.addPage();
        yPos = 20;
      }
      doc.setTextColor(30, 58, 138);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('SKILLS', 15, yPos);
      yPos += 2;
      doc.setDrawColor(30, 58, 138);
      doc.line(15, yPos, 35, yPos);
      yPos += 6;

      doc.setTextColor(60, 60, 60);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const skillsList = cvData.skills.split(',').map(s => s.trim()).filter(Boolean);
      const skillsText = skillsList.join('  •  ');
      const skillsLines = doc.splitTextToSize(skillsText, pageWidth - 30);
      doc.text(skillsLines, 15, yPos);
      yPos += skillsLines.length * 5 + 8;
    }

    // Education Section
    if (cvData.education) {
      if (yPos > 260) {
        doc.addPage();
        yPos = 20;
      }
      doc.setTextColor(30, 58, 138);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('EDUCATION', 15, yPos);
      yPos += 2;
      doc.setDrawColor(30, 58, 138);
      doc.line(15, yPos, 40, yPos);
      yPos += 6;

      doc.setTextColor(60, 60, 60);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const eduLines = doc.splitTextToSize(cvData.education, pageWidth - 30);
      doc.text(eduLines, 15, yPos);
      yPos += eduLines.length * 5 + 8;
    }

    // Experience Section
    if (cvData.experience) {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      doc.setTextColor(30, 58, 138);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('WORK EXPERIENCE', 15, yPos);
      yPos += 2;
      doc.setDrawColor(30, 58, 138);
      doc.line(15, yPos, 55, yPos);
      yPos += 6;

      doc.setTextColor(60, 60, 60);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const expLines = doc.splitTextToSize(cvData.experience, pageWidth - 30);
      doc.text(expLines, 15, yPos);
    }

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('Generated by BientoMart Learning Hub', pageWidth / 2, 290, { align: 'center' });

    doc.save(`${cvData.fullName || 'my'}-CV.pdf`);
  };

  const handleDownloadClick = () => {
    if (!hasPaidForCV) {
      onPaymentRequired();
    } else {
      downloadPDF();
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Form Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-2xl overflow-hidden"
        >
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-7 h-7" />
              <h2 className="text-2xl font-bold">CV Builder</h2>
            </div>
            <p className="text-indigo-100">
              Fill in your details to generate a professional CV
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Personal Details */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-indigo-600" />
                Personal Details
              </h3>
              <div className="space-y-3">
                <input
                  type="text"
                  name="fullName"
                  value={cvData.fullName}
                  onChange={handleChange}
                  placeholder="Full Name"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors"
                />
                <input
                  type="email"
                  name="email"
                  value={cvData.email}
                  onChange={handleChange}
                  placeholder="Email Address"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="tel"
                    name="phone"
                    value={cvData.phone}
                    onChange={handleChange}
                    placeholder="Phone Number"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors"
                  />
                  <input
                    type="text"
                    name="location"
                    value={cvData.location}
                    onChange={handleChange}
                    placeholder="Location"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Education */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <School className="w-5 h-5 text-indigo-600" />
                Education
              </h3>
              <select
                name="educationLevel"
                value={cvData.educationLevel}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors mb-3"
              >
                <option value="">Select Education Level</option>
                <option value="beginner">Beginner</option>
                <option value="ordinary">Ordinary Level</option>
                <option value="advanced">Advanced Level</option>
                <option value="other">Other</option>
              </select>
              <textarea
                name="education"
                value={cvData.education}
                onChange={handleChange}
                placeholder="e.g., Diploma in IT from Makerere University (2020-2022)"
                rows={3}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors resize-none"
              />
            </div>

            {/* Skills */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Star className="w-5 h-5 text-indigo-600" />
                Skills
              </h3>
              <textarea
                name="skills"
                value={cvData.skills}
                onChange={handleChange}
                placeholder="e.g., Microsoft Office, Web Development, Digital Marketing, Communication"
                rows={3}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors resize-none"
              />
            </div>

            {/* Experience */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-indigo-600" />
                Work Experience <span className="text-gray-400 font-normal">(Optional)</span>
              </h3>
              <textarea
                name="experience"
                value={cvData.experience}
                onChange={handleChange}
                placeholder="e.g., Data Entry Clerk at ABC Company (2023-Present)"
                rows={3}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors resize-none"
              />
            </div>

            {/* Summary */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600" />
                Professional Summary <span className="text-gray-400 font-normal">(Optional)</span>
              </h3>
              <textarea
                name="summary"
                value={cvData.summary}
                onChange={handleChange}
                placeholder="Brief summary about yourself and career goals..."
                rows={4}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors resize-none"
              />
            </div>

            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
            >
              <FileText className="w-5 h-5" />
              Generate CV Preview
            </motion.button>
          </form>
        </motion.div>

        {/* CV Preview */}
        <div>
          {previewMode && (
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="sticky top-24"
            >
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-4 text-white flex items-center justify-between">
                  <h3 className="font-bold">CV Preview</h3>
                  <motion.button
                    onClick={downloadPDF}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 rounded-lg text-sm font-semibold hover:shadow-lg transition-all"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </motion.button>
                </div>

                <div
                  ref={cvRef}
                  className="p-8 bg-white"
                  style={{ fontFamily: 'Arial, sans-serif' }}
                >
                  {/* CV Header */}
                  <div style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%)' }} className="p-6 rounded-2xl text-white mb-6">
                    <h1 className="text-3xl font-bold mb-2">{cvData.fullName || 'Your Name'}</h1>
                    <div className="text-sm opacity-90 space-y-1">
                      {cvData.email && <div>📧 {cvData.email}</div>}
                      {cvData.phone && <div>📱 {cvData.phone}</div>}
                      {cvData.location && <div>📍 {cvData.location}</div>}
                    </div>
                  </div>

                  {/* Professional Summary */}
                  {cvData.summary && (
                    <div className="mb-6">
                      <h2 className="text-lg font-bold text-gray-900 mb-2 pb-2 border-b-2 border-indigo-600">
                        PROFESSIONAL SUMMARY
                      </h2>
                      <p className="text-gray-700 leading-relaxed">{cvData.summary}</p>
                    </div>
                  )}

                  {/* Skills */}
                  {cvData.skills && (
                    <div className="mb-6">
                      <h2 className="text-lg font-bold text-gray-900 mb-2 pb-2 border-b-2 border-indigo-600">
                        SKILLS
                      </h2>
                      <div className="flex flex-wrap gap-2">
                        {cvData.skills.split(',').map((skill, i) => (
                          <span
                            key={i}
                            className="px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-medium"
                          >
                            {skill.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Education */}
                  {cvData.education && (
                    <div className="mb-6">
                      <h2 className="text-lg font-bold text-gray-900 mb-2 pb-2 border-b-2 border-indigo-600">
                        EDUCATION
                      </h2>
                      <p className="text-gray-700">{cvData.education}</p>
                    </div>
                  )}

                  {/* Experience */}
                  {cvData.experience && (
                    <div className="mb-6">
                      <h2 className="text-lg font-bold text-gray-900 mb-2 pb-2 border-b-2 border-indigo-600">
                        WORK EXPERIENCE
                      </h2>
                      <p className="text-gray-700">{cvData.experience}</p>
                    </div>
                  )}

                  {!cvData.summary && !cvData.skills && !cvData.education && !cvData.experience && (
                    <div className="text-center py-12 text-gray-400">
                      <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p>Fill in additional details to see your CV preview</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CVAssistant;
